import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import api from "../lib/api";

const WD = ["lun", "mar", "mer", "gio", "ven", "sab", "dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function mondayIndex(dow) { return (dow + 6) % 7; } // JS Sun=0 -> Mon-based
function prevIso(k) { const d = new Date(k + "T00:00:00"); d.setDate(d.getDate() - 1); return iso(d); }

const GREEN = "#f0f7ee";  // available half
const RED = "#fbdada";    // occupied half

function dayStyle(amBusy, pmBusy) {
  // top-left = morning (departure side), bottom-right = evening (arrival side)
  const a = amBusy ? RED : GREEN;
  const b = pmBusy ? RED : GREEN;
  if (a === b) return { background: a };
  return { background: `linear-gradient(135deg, ${a} 0 50%, ${b} 50% 100%)` };
}

function MonthGrid({ monthDate, unavailable, todayIso, endIso, onPick }) {
  const y = monthDate.getFullYear(), m = monthDate.getMonth();
  const first = new Date(y, m, 1);
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const lead = mondayIndex(first.getDay());
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));

  return (
    <div className="flex-1 min-w-[260px]">
      <p className="text-center font-display text-lg text-fir mb-2">{MONTHS[m]} {y}</p>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WD.map((w) => <div key={w} className="text-center text-[11px] uppercase tracking-wide text-fir-deep/40">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const k = iso(d);
          const past = k < todayIso;
          const outOfRange = k >= endIso;
          const pmBusy = unavailable.has(k);          // night starting today (arrival side)
          const amBusy = unavailable.has(prevIso(k)); // night ending today (departure side)
          const selectable = !past && !outOfRange && !pmBusy; // can start a stay tonight
          const state = past || outOfRange ? "past" : (amBusy && pmBusy) ? "busy" : (amBusy || pmBusy) ? "changeover" : "free";
          return (
            <button
              key={i}
              type="button"
              data-testid={`cal-day-${k}`}
              data-state={state}
              disabled={!selectable}
              onClick={() => selectable && onPick && onPick(k)}
              style={past || outOfRange ? undefined : dayStyle(amBusy, pmBusy)}
              className={[
                "relative h-9 rounded-lg text-sm flex items-center justify-center transition-all",
                past || outOfRange ? "text-fir-deep/25 cursor-default bg-transparent" : "",
                state === "busy" ? "text-red-500/70 line-through cursor-not-allowed" : "",
                state === "changeover" ? "text-fir font-medium cursor-pointer hover:ring-2 hover:ring-gold" : "",
                state === "free" ? "text-fir cursor-pointer hover:ring-2 hover:ring-gold" : "",
              ].join(" ")}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AvailabilityCalendar({ apartmentId, roomTypeId = null, onPick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0); // month offset from current

  useEffect(() => {
    setLoading(true);
    const params = { apartment_id: apartmentId, months: 12 };
    if (roomTypeId) params.room_type_id = roomTypeId;
    api.get("/availability/calendar", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ unavailable: [], start: iso(new Date()), end: iso(new Date()) }))
      .finally(() => setLoading(false));
  }, [apartmentId, roomTypeId]);

  const unavailable = useMemo(() => new Set(data?.unavailable || []), [data]);
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const nextM = new Date(now.getFullYear(), now.getMonth() + offset + 1, 1);
  const maxOffset = data ? Math.max(0, (data.months || 6) - 2) : 4;

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-fir" /></div>;

  return (
    <div data-testid="availability-calendar" className="bg-white rounded-3xl border border-wood/30 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="cal-prev" type="button" disabled={offset <= 0} onClick={() => setOffset((o) => Math.max(0, o - 1))}
          className="p-2 rounded-lg border border-stone text-fir hover:bg-stone/40 disabled:opacity-30"><ChevronLeft size={18} /></button>
        <span className="text-sm text-fir-deep/60">Disponibilità in tempo reale</span>
        <button data-testid="cal-next" type="button" disabled={offset >= maxOffset} onClick={() => setOffset((o) => Math.min(maxOffset, o + 1))}
          className="p-2 rounded-lg border border-stone text-fir hover:bg-stone/40 disabled:opacity-30"><ChevronRight size={18} /></button>
      </div>
      <div className="flex flex-col md:flex-row gap-8">
        <MonthGrid monthDate={base} unavailable={unavailable} todayIso={data.start} endIso={data.end} onPick={onPick} />
        <MonthGrid monthDate={nextM} unavailable={unavailable} todayIso={data.start} endIso={data.end} onPick={onPick} />
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 pt-4 border-t border-stone text-xs text-fir-deep/70">
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded inline-block" style={{ background: GREEN }} /> Disponibile</span>
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded inline-block" style={{ background: RED }} /> Occupato</span>
        <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded inline-block" style={{ background: `linear-gradient(135deg, ${RED} 0 50%, ${GREEN} 50% 100%)` }} /> Giorno di cambio (arrivo/partenza)</span>
        <span className="text-fir-deep/40">Clicca una data disponibile per iniziare la prenotazione</span>
      </div>
    </div>
  );
}
